/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Container } from "react-awesome-styled-grid";
import { useParams } from "react-router-dom";
import { Box, Loader, Text, theme } from "@nordcloud/gnui";
import { Breadcrumbs, showSuccess } from "~/components";
import { BillingRulesFormEnvironment } from "~/components/BillingRules/BillingRulesFormEnvironment";
import { TabsPath } from "~/components/Customer/CustomerNavigation";
import { DiscoveryRules } from "~/components/Environment/DiscoveryRules";
import { EnvironmentDetailsInfo } from "~/components/Environment/EnvironmentDetailsInfo";
import { EnvironmentInitialState } from "~/components/Environment/initialState";
import { useShowError } from "~/components/Error/useShowError";
import { InlineEditText } from "~/components/InlineEdit/InlineEditText";
import { ServiceEnvironment } from "~/components/Services/ServiceEnvironment";
import { ServiceAttachment } from "~/components/Services/types";
import { DetailRow } from "~/components/Utils/DetailRow";
import { useUserConsumer } from "~/context/User/User";
import { Environment, EnvironmentRouteParams } from "~/models/environment";
import { isStandardUser } from "~/routing";
import { generateActionSuccessText, useAxios } from "~/tools";

function EnvironmentDetails() {
  const state = useParams<EnvironmentRouteParams>();
  const { environmentId } = state;
  const [environment, setEnvironment] = React.useState<Environment>(
    EnvironmentInitialState
  );
  const [envLoading, setEnvLoading] = React.useState(true);
  const [servicesLoading, setServicesLoading] = React.useState(true);
  const [dateError, setDateError] = React.useState(false);

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const { role } = useUserConsumer();
  const canEdit = isStandardUser(role);

  const tags = [
    {
      color: theme.color.support.purple,
      text: "Environment",
    },
  ];

  const fetchEnvironment = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(`/environments/${environmentId}`);
      setEnvironment(data);
    } catch (e) {
      showError(e.response);
    } finally {
      setEnvLoading(false);
    }
  }, [Axios, environmentId, showError, setEnvLoading]);

  React.useEffect(() => {
    fetchEnvironment();
  }, [fetchEnvironment]);

  const [serviceAttachments, setServiceAttachments] = React.useState<
    ServiceAttachment[]
  >([]);

  const fetchServicesAttachments = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(
        `/environments/${environmentId}/services`
      );
      setServiceAttachments(data?.attachedServices);
    } catch (e) {
      showError(e.response);
    } finally {
      setServicesLoading(false);
    }
  }, [Axios, environmentId, showError, setServicesLoading]);

  const onSubmit = async (values: { cfRepoPath: string }) => {
    const organizeValues = { ...environment, cfRepoPath: values.cfRepoPath };

    try {
      await Axios().put(`/environments/${environment.id}`, organizeValues);
      showSuccess(
        generateActionSuccessText(`Environment ${environment.name}`)()(
          "updated"
        )()
      );
    } catch (e) {
      showError(e.response);
    } finally {
      fetchEnvironment();
    }
  };

  React.useEffect(() => {
    fetchServicesAttachments();
  }, [fetchServicesAttachments]);

  const initialBreadcrumbs = [
    { label: "Customers", uri: "/" },
    {
      uri: `/customers/${environment?.customer?.id}/${TabsPath.INFO}`,
      label: environment.customer.name,
    },
  ];
  const breadcrumbLabels = !environment?.generalMapping
    ? [
        ...initialBreadcrumbs,
        {
          uri: `/customers/${environment.customer.id}/applications`,
          label: "Applications",
        },
        {
          uri: `/customers/${environment.customer.id}/applications/${environment.application.id}`,
          label: environment.application.name,
        },
        { uri: environment.id, label: environment?.name },
      ]
    : [
        ...initialBreadcrumbs,
        { uri: environment.id, label: environment?.name },
      ];

  if (envLoading) {
    return (
      <Container>
        <Loader inContent />
      </Container>
    );
  }

  return (
    <Container>
      {environment.application.name.length !== 0 && (
        <Breadcrumbs
          title={environment?.name || ""}
          tags={tags}
          customBreadcrumbs={breadcrumbLabels || [{ label: "", uri: "" }]}
          generalMapping={environment?.generalMapping}
        />
      )}
      <EnvironmentDetailsInfo
        environment={environment}
        setDateError={setDateError}
        dateError={dateError}
        fetchEnvironment={fetchEnvironment}
        canEdit={canEdit}
      />
      {!environment.generalMapping && <DiscoveryRules />}
      <ServiceEnvironment
        serviceAttachments={serviceAttachments}
        fetchServicesAttachments={fetchServicesAttachments}
        envId={environment?.id}
        envName={environment?.name}
        isLoading={servicesLoading}
        refetch={fetchServicesAttachments}
        canEdit={canEdit}
      />
      <Box spacing="spacing04">
        <Text weight="medium">Cloud Foundation</Text>
        <DetailRow title="Cloud Foundation Repository Path" minWidth={"18rem"}>
          <InlineEditText
            name="cfRepoPath"
            defaultValue={environment.cfRepoPath}
            canEdit={canEdit}
            onSubmit={(value) => onSubmit({ cfRepoPath: value })}
          />
        </DetailRow>
      </Box>
      <BillingRulesFormEnvironment
        id={environment?.id}
        name={environment?.name}
        environment={environment}
        refetch={fetchEnvironment}
        dateError={dateError}
        canEdit={canEdit}
      />
    </Container>
  );
}

export { EnvironmentDetails };
